import { BaseExplorerConfig, FieldTransformer } from '../../../types/baseExplorerConfig';
import { LightningEntity } from '../../../types/lightning';
import { lightningServiceAdapter } from './lightningServiceAdapter';
import { faBolt } from '@fortawesome/free-solid-svg-icons';

const dateTimeTransformer: FieldTransformer = {
  transform: (value: string | null) => {
    if (!value) return 'N/A';
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      return d.toLocaleString('en-CA', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false,
        timeZoneName: 'short',
      });
    } catch {
      return value;
    }
  },
  reverseTransform: (value: any) => String(value || ''),
};

const peakCurrentTransformer: FieldTransformer = {
  transform: (value: number | null) => {
    if (value === null || value === undefined) return 'N/A';
    return `${value.toFixed(1)} kA`;
  },
  reverseTransform: (value: any) => {
    const str = String(value || '').replace(' kA', '');
    return isNaN(Number(str)) ? 0 : Number(str);
  },
};

import { STRIKE_CHARGE_LABELS, CLOUD_INDICATOR_LABELS } from '../../../shared/lightningTransforms';
import { LIGHTNING_COLUMNS } from '../../../shared/lightningSchema';
import { gridColumns } from '../../../shared/columnSchema';

const cloudTransformer: FieldTransformer = {
  transform: (value: number | null) =>
    value != null ? (CLOUD_INDICATOR_LABELS[value] ?? String(value)) : 'Unknown',
  reverseTransform: (value: any) => String(value || ''),
};

const chargeTransformer: FieldTransformer = {
  transform: (value: string | null) =>
    value ? (STRIKE_CHARGE_LABELS[value] ?? value) : 'Unknown',
  reverseTransform: (value: any) => String(value || ''),
};

const typeTransformer: FieldTransformer = {
  transform: (value: string | null) => {
    if (value === 'test') return 'Test';
    if (value === 'strike') return 'Strike';
    return value || 'Unknown';
  },
  reverseTransform: (value: any) => String(value || ''),
};

// Hourly test strike: fixed location on Great Slave Lake, -14 kA
const isTestStrike = (entity: LightningEntity): boolean =>
  entity.lat === 61.5004 && entity.lon === -114.5009 && entity.peakCurrent === -14;

export const createLightningConfig = (): BaseExplorerConfig<LightningEntity> => ({
  entityName: 'Lightning Strike',
  complexityLevel: 'moderate',
  autoRefreshIntervalMs: 300000,

  displayFields: [
    'strikeId', 'type', 'ualfVersion', 'strikeDate', 'lat', 'lon',
    'peakCurrent', 'strikeCharge', 'multiplicity',
    'originatingSensors', 'cloudIndicator', 'angleIndicator', 'signalIndicator',
  ],

  getRowClassName: (entity: LightningEntity) => isTestStrike(entity) ? 'lightning-test-strike' : undefined,

  serviceAdapter: lightningServiceAdapter,

  fieldTransformers: {
    strikeDate: dateTimeTransformer,
    peakCurrent: peakCurrentTransformer,
    strikeCharge: chargeTransformer,
    cloudIndicator: cloudTransformer,
    type: typeTransformer,
  } as any,

  features: {
    pagination: {
      enabled: true,
      defaultPageSize: 50,
      pageSizeOptions: [25, 50, 100, 200],
      showPageInfo: true,
    },
    sorting: {
      enabled: true,
      multiColumn: false,
      defaultSort: [{ field: 'strikeDate', direction: 'desc' }],
    },
    searching: {
      enabled: true,
      placeholder: 'Search by charge type, peak current...',
      searchFields: ['strikeCharge', 'peakCurrent'],
      debounceMs: 300,
    },
    filtering: {
      enabled: true,
      advancedFilters: false,
      quickFilters: [
        {
          field: 'type',
          label: 'Type',
          type: 'dropdown',
          options: [
            { value: 'test', label: 'Test' },
            { value: 'strike', label: 'Strike' },
          ],
        },
        { field: 'strikeDate', label: 'Date/Time', type: 'dateRange' },
        {
          field: 'strikeCharge',
          label: 'Charge',
          type: 'dropdown',
          options: [
            { value: 'P', label: 'Positive' },
            { value: 'N', label: 'Negative' },
          ],
        },
        { field: 'peakCurrent', label: 'Peak Current', type: 'numeric' },
        { field: 'lat', label: 'Latitude', type: 'numeric' },
        { field: 'lon', label: 'Longitude', type: 'numeric' },
        { field: 'multiplicity', label: 'Multiplicity', type: 'numeric' },
        { field: 'originatingSensors', label: 'Sensors', type: 'numeric' },
        {
          field: 'cloudIndicator',
          label: 'Cloud Indicator',
          type: 'dropdown',
          options: [
            { value: '1', label: 'Cloud-to-Cloud' },
            { value: '0', label: 'Cloud-to-Ground' },
          ],
        },
        { field: 'angleIndicator', label: 'Angle Indicator', type: 'numeric' },
        { field: 'signalIndicator', label: 'Signal Indicator', type: 'numeric' },
      ],
    },
    export: {
      enabled: true,
      formats: ['csv'],
      includeFilters: true,
      entity: 'lightning'
    },
    editing: {
      enabled: false,
      allowCreate: false,
      allowUpdate: false,
      allowDelete: false,
      mode: 'modal',
    },
  },

  presentation: {
    title: 'Lightning Strikes',
    subtitle: 'Real-time lightning strike data',
    icon: faBolt,
    columnConfig: gridColumns(LIGHTNING_COLUMNS),
    styling: {
      theme: 'default',
      striped: true,
      bordered: true,
      hoverable: true,
    },
    layout: {
      responsive: true,
      showFooter: true,
      showToolbar: true,
      minHeight: 400,
      maxHeight: 800,
    },
  },
});
